import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";

import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import WifiIcon from "@material-ui/icons/Wifi";
import WifiOffIcon from "@material-ui/icons/WifiOff";
import TimelineIcon from "@material-ui/icons/Timeline";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import CarDetails from "superAdminViews/ride/drivers/CarDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import TableToolbar from "designV2/components/TableToolbar";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { readableError } from "../detailFields";
import "../superRide.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const CAR_FILTERS = `
  _id: $_id
  plate: $plate
  ride: $ride
  delivery: $delivery
  brand: $brand
  model: $model
  carType: $carType
  carStatus: $carStatus
  createdAt: $createdAt
  driverFullName: $driverFullName
  driverPhoneNumber: $driverPhoneNumber
`;

const CAR_VARIABLES = `
  $limit: Int
  $skip: Int
  $_id: ID
  $plate: String
  $ride: Boolean
  $delivery: Boolean
  $brand: ID
  $model: ID
  $carType: ID
  $carStatus: CarStatus
  $createdAt: Date
  $driverFullName: String
  $driverPhoneNumber: String
  $createdAtSort: Int
`;

const GET_CARS = gql`
  query getCarsByAdmin(${CAR_VARIABLES}) {
    getCarsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${CAR_FILTERS} }
      sort: { createdAt: $createdAtSort }
    ) {
      _id
      plate
      status
      ride
      delivery
      manufacturingYear
      description
      registrationDocumentUrl
      owner {
        _id
        fullName
        phoneNumber
        email
      }
      carType {
        _id
        type
      }
      color {
        _id
        name
      }
      brand {
        _id
        name
      }
      model {
        _id
        name
      }
      pictures {
        inner {
          url
        }
        outer {
          url
        }
      }
      insurance {
        insuranceImageUrl
        expireDate
      }
      carOptions {
        inHurry
        orderingForSomeoneElse
        pet
        bagsWithMe
        reserved
        airConditioner
        welcomeSign
        driverAssistant
        withInfant
        waitTimesInMinutes
        tipValue
      }
    }
    getCarsCountByAdmin(filters: { ${CAR_FILTERS} })
  }
`;

const GET_FLEET_COUNTS = gql`
  query getFleetCounts {
    online: getCarsCountByAdmin(filters: { carStatus: ONLINE })
    offline: getCarsCountByAdmin(filters: { carStatus: OFFLINE })
    inTrip: getCarsCountByAdmin(filters: { carStatus: INTRIP })
  }
`;

const GET_LOOKUPS = gql`
  query getVehicleLookups {
    getCarBrands {
      _id
      name
    }
    getCarTypes {
      _id
      type
    }
  }
`;

const GET_MODELS = gql`
  query getCarModels($brand: ID) {
    getCarModels(filters: { brand: $brand }) {
      _id
      name
    }
  }
`;

const DELETE_CARS = gql`
  mutation deleteCarByAdmin($idSet: [ID!]!) {
    deleteCarByAdmin(idSet: $idSet) {
      _id
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const STATUS_OPTIONS = [
  { value: "ONLINE", label: "Online" },
  { value: "OFFLINE", label: "Offline" },
  { value: "INTRIP", label: "On a trip" },
];
const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const STATUS_TONES = { ONLINE: "success", OFFLINE: "neutral", INTRIP: "info" };

const USE_OPTIONS = [
  { value: "", label: "Any use" },
  { value: "ride", label: "Rides" },
  { value: "delivery", label: "Deliveries" },
];
const USE_LABELS = USE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SEARCH_TARGETS = [
  { value: "plate", label: "Plate" },
  { value: "driver", label: "Driver" },
];
const SEARCH_LABELS = { plate: "Plate", driver: "Driver" };

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

const FILTER_DEBOUNCE_MS = 350;

const looksLikePhone = (value) => /^[\d\s+()-]+$/.test(value);
const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");

// An insurance document that has lapsed is the one thing on this screen
// worth chasing, so it is surfaced on the row rather than buried in the
// details dialog.
const insuranceState = (car) => {
  const expiry = car.insurance && car.insurance.expireDate;
  if (!expiry) return null;
  const days = moment(expiry).diff(moment(), "days");
  if (days < 0) return { tone: "danger", label: "Insurance expired" };
  if (days <= 30) return { tone: "warning", label: `Insurance ${days}d left` };
  return null;
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Cars = () => {
  const [searchTarget, setSearchTarget] = useState("plate");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [useFilter, setUseFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [carTypeFilter, setCarTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [carDetail, setCarDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({ limit: perPage, skip: 0, createdAtSort: -1 });

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      const search = {};
      if (term) {
        if (searchTarget === "plate") search.plate = term;
        else if (looksLikePhone(term)) search.driverPhoneNumber = term;
        else search.driverFullName = term;
      }

      setVariables((v) => ({
        ...v,
        skip: 0,
        plate: undefined,
        driverFullName: undefined,
        driverPhoneNumber: undefined,
        ...search,
        carStatus: statusFilter || undefined,
        ride: useFilter === "ride" ? true : undefined,
        delivery: useFilter === "delivery" ? true : undefined,
        brand: brandFilter || undefined,
        model: modelFilter || undefined,
        carType: carTypeFilter || undefined,
        createdAt: dateTo || undefined,
        createdAtSort: sortOption === "oldest" ? 1 : -1,
      }));
      setPageCount(0);
      setSelectedIds([]);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [
    searchTarget,
    searchTerm,
    statusFilter,
    useFilter,
    brandFilter,
    modelFilter,
    carTypeFilter,
    dateFrom,
    dateTo,
    sortOption,
  ]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
    setSelectedIds([]);
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_CARS, {
    variables,
    fetchPolicy: "network-only",
  });
  const { data: countsData, refetch: refetchCounts } = useQuery(GET_FLEET_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const { data: lookupData } = useQuery(GET_LOOKUPS, { errorPolicy: "all" });
  const [loadModels, { data: modelData }] = useLazyQuery(GET_MODELS);
  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_CARS, {
    fetchPolicy: "network-only",
  });
  const [deleteCarByAdmin] = useMutation(DELETE_CARS);

  // Models only make sense once a brand is chosen, so the list is loaded
  // for that brand rather than fetching every model up front.
  useEffect(() => {
    setModelFilter("");
    if (brandFilter) loadModels({ variables: { brand: brandFilter } });
  }, [brandFilter, loadModels]);

  const cars = (data && data.getCarsByAdmin) || [];
  const totalCount = (data && data.getCarsCountByAdmin) || 0;
  const counts = countsData || {};

  const brandOptions = useMemo(
    () =>
      ((lookupData && lookupData.getCarBrands) || []).map((b) => ({ value: b._id, label: b.name })),
    [lookupData]
  );
  const carTypeOptions = useMemo(
    () =>
      ((lookupData && lookupData.getCarTypes) || []).map((t) => ({ value: t._id, label: t.type })),
    [lookupData]
  );
  const modelOptions = useMemo(
    () => ((modelData && modelData.getCarModels) || []).map((m) => ({ value: m._id, label: m.name })),
    [modelData]
  );

  const labelFor = (options, value, fallback) => {
    const found = options.find((o) => o.value === value);
    return found ? found.label : fallback;
  };

  /* ---------------- selection + delete ---------------- */

  const toggleSelectOne = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
    );

  const toggleSelectAll = () => {
    const ids = cars.map((c) => c._id);
    setSelectedIds((current) => (current.length === ids.length ? [] : ids));
  };

  const checkboxState =
    cars.length > 0 && selectedIds.length === cars.length
      ? "checked"
      : selectedIds.length > 0
      ? "indeterminate"
      : "unchecked";

  const runDelete = () => {
    const targets = deleteTarget || [];
    setDeleting(true);
    setActionError("");
    deleteCarByAdmin({ variables: { idSet: targets.map((c) => c._id) } })
      .then(() => {
        setDeleting(false);
        setDeleteTarget(null);
        setSelectedIds([]);
        refetch();
        refetchCounts();
      })
      .catch((err) => {
        setDeleting(false);
        setDeleteTarget(null);
        setActionError(readableError(err));
      });
  };

  /* ---------------- filter chips ---------------- */

  const trimmedTerm = searchTerm.trim();
  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }
  if (statusFilter) activeFilterChips.push({ id: "status", label: `Status: ${STATUS_LABELS[statusFilter]}` });
  if (useFilter) activeFilterChips.push({ id: "use", label: USE_LABELS[useFilter] });
  if (brandFilter) {
    activeFilterChips.push({ id: "brand", label: `Brand: ${labelFor(brandOptions, brandFilter, "-")}` });
  }
  if (modelFilter) {
    activeFilterChips.push({ id: "model", label: `Model: ${labelFor(modelOptions, modelFilter, "-")}` });
  }
  if (carTypeFilter) {
    activeFilterChips.push({
      id: "carType",
      label: `Type: ${labelFor(carTypeOptions, carTypeFilter, "-")}`,
    });
  }
  if (dateTo) activeFilterChips.push({ id: "date", label: `Added before ${dateTo}` });

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "status") setStatusFilter("");
    if (id === "use") setUseFilter("");
    if (id === "brand") setBrandFilter("");
    if (id === "model") setModelFilter("");
    if (id === "carType") setCarTypeFilter("");
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setUseFilter("");
    setBrandFilter("");
    setModelFilter("");
    setCarTypeFilter("");
    setDateFrom("");
    setDateTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [
      ["Plate", "Owner", "Phone", "Brand", "Model", "Year", "Type", "Colour", "Status", "Rides", "Deliveries", "Insurance expiry"],
    ];
    exportData.getCarsByAdmin.forEach((car) => {
      rows.push([
        car.plate || "-",
        (car.owner && car.owner.fullName) || "-",
        (car.owner && car.owner.phoneNumber) || "-",
        (car.brand && car.brand.name) || "-",
        (car.model && car.model.name) || "-",
        car.manufacturingYear || "-",
        (car.carType && car.carType.type) || "-",
        (car.color && car.color.name) || "-",
        STATUS_LABELS[car.status] || car.status || "-",
        car.ride ? "yes" : "no",
        car.delivery ? "yes" : "no",
        car.insurance && car.insurance.expireDate
          ? moment(car.insurance.expireDate).format("MM/DD/YYYY")
          : "-",
      ]);
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Vehicles");
    XLSX.writeFile(wb, "Vehicles.xlsx");
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (totalCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: totalCount } });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superRidePage superCarsPage">
        <main className="main">
          <PageHeader title="Vehicles" description="Every vehicle registered to a driver." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={8} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  const selectedCars = cars.filter((c) => selectedIds.includes(c._id));

  return (
    <div className="page superRidePage superCarsPage">
      <main className="main">
        <PageHeader
          title="Vehicles"
          count={totalCount}
          description="Every vehicle registered to a driver, what it is cleared to carry, and whether it is online."
          actions={
            <React.Fragment>
              <PageActionButton
                icon={<CloudDownloadIcon fontSize="small" />}
                label={exporting ? "Exporting" : "Export"}
                info={exporting ? "Preparing file" : `${totalCount} vehicle${totalCount === 1 ? "" : "s"}`}
                onClick={handleExport}
                disabled={totalCount === 0}
                loading={exporting}
              />
              <Button
                variant="primary"
                onClick={() => (window.location.href = `${endPoint}/superAdmin/addVehicle`)}
              >
                Add vehicle
              </Button>
            </React.Fragment>
          }
        />

        <section className="stats">
          <MetricCard icon={<DirectionsCarIcon />} title="Matching vehicles" value={totalCount} />
          <MetricCard
            icon={<WifiIcon />}
            title="Online"
            value={counts.online || 0}
            tone={counts.online > 0 ? "success" : "default"}
          />
          <MetricCard icon={<TimelineIcon />} title="On a trip" value={counts.inTrip || 0} tone="info" />
          <MetricCard icon={<WifiOffIcon />} title="Offline" value={counts.offline || 0} />
        </section>

        <section className="filters">
          <SearchField
            placeholder={
              searchTarget === "plate"
                ? "Search by plate..."
                : "Search by driver name or phone number..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <FilterDropdown
              label={statusFilter ? `Status: ${STATUS_LABELS[statusFilter]}` : "Status"}
              options={[{ value: "", label: "Any status" }, ...STATUS_OPTIONS]}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
            <FilterDropdown
              label={useFilter ? USE_LABELS[useFilter] : "Use"}
              options={USE_OPTIONS}
              value={useFilter}
              onChange={(e) => setUseFilter(e.target.value)}
            />
            <FilterDropdown
              label={brandFilter ? `Brand: ${labelFor(brandOptions, brandFilter, "-")}` : "Brand"}
              options={[{ value: "", label: "All brands" }, ...brandOptions]}
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
            />
            <FilterDropdown
              label={
                !brandFilter
                  ? "Model"
                  : modelFilter
                  ? `Model: ${labelFor(modelOptions, modelFilter, "-")}`
                  : "Model"
              }
              options={
                brandFilter
                  ? [{ value: "", label: "All models" }, ...modelOptions]
                  : [{ value: "", label: "Pick a brand first" }]
              }
              value={modelFilter}
              onChange={(e) => setModelFilter(e.target.value)}
            />
            <FilterDropdown
              label={carTypeFilter ? `Type: ${labelFor(carTypeOptions, carTypeFilter, "-")}` : "Type"}
              options={[{ value: "", label: "All types" }, ...carTypeOptions]}
              value={carTypeFilter}
              onChange={(e) => setCarTypeFilter(e.target.value)}
            />
            <DateRangeFilter
              label="Added"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            <Button
              variant="dangerQuiet"
              size="sm"
              icon={<DeleteIcon fontSize="small" />}
              onClick={() => setDeleteTarget(selectedCars)}
            >
              Delete selected
            </Button>
          }
          rightActions={
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              title="Refresh vehicles"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superRideSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox">
                <Checkbox
                  size="small"
                  checked={checkboxState === "checked"}
                  indeterminate={checkboxState === "indeterminate"}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select every vehicle on this page" }}
                />
              </div>
              <div className="tableHeaderCell">Plate</div>
              <div className="tableHeaderCell">Driver</div>
              <div className="tableHeaderCell">Vehicle</div>
              <div className="tableHeaderCell">Cleared for</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell">Insurance</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {cars.length === 0 ? (
              <EmptyState
                icon={<DirectionsCarIcon />}
                title={activeFilterChips.length === 0 ? "No vehicles yet" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Vehicles appear here once drivers register them."
                    : "No vehicles match these filters. Try widening or clearing them."
                }
              />
            ) : (
              cars.map((car) => {
                const insurance = insuranceState(car);
                return (
                  <div className="row" key={car._id}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(car._id)}
                      onChange={() => toggleSelectOne(car._id)}
                      inputProps={{ "aria-label": `Select ${car.plate}` }}
                    />

                    <span className="superRideMono" data-label="Plate">
                      {car.plate || "-"}
                    </span>

                    <div className="superRidePerson" data-label="Driver">
                      {car.owner ? (
                        <React.Fragment>
                          <span className="superRidePersonName">{car.owner.fullName}</span>
                          <span className="superRidePersonMeta">{car.owner.phoneNumber}</span>
                        </React.Fragment>
                      ) : (
                        <span className="superRideMuted">No owner</span>
                      )}
                    </div>

                    <div className="superRidePerson" data-label="Vehicle">
                      <span className="superRidePersonName">
                        {[car.brand && car.brand.name, car.model && car.model.name]
                          .filter(Boolean)
                          .join(" ") || "Unspecified"}
                      </span>
                      <span className="superRidePersonMeta">
                        {[car.manufacturingYear, car.color && car.color.name, car.carType && car.carType.type]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>

                    <span className="superRideStack" data-label="Cleared for">
                      {car.ride && <span className="superRideTag">Rides</span>}
                      {car.delivery && <span className="superRideTag">Deliveries</span>}
                      {!car.ride && !car.delivery && <span className="superRideMuted">Neither</span>}
                    </span>

                    <span data-label="Status">
                      <StatusBadge
                        status={STATUS_TONES[car.status] || "neutral"}
                        label={STATUS_LABELS[car.status] || car.status || "Unknown"}
                      />
                    </span>

                    <span data-label="Insurance">
                      {insurance ? (
                        <StatusBadge status={insurance.tone} label={insurance.label} dot={false} />
                      ) : car.insurance && car.insurance.expireDate ? (
                        <span className="superRideMuted">
                          {formatDate(car.insurance.expireDate)}
                        </span>
                      ) : (
                        <span className="superRideMuted">Not on file</span>
                      )}
                    </span>

                    <div className="actions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() =>
                          (window.location.href = `${endPoint}/superAdmin/editVehicle/${car._id}`)
                        }
                        title="Edit vehicle"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setCarDetail(car)}
                        title="Vehicle details"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(carDetail)}
        onClose={() => setCarDetail(null)}
        title="Vehicle details"
        subtitle={carDetail ? carDetail.plate : ""}
        size="lg"
      >
        {carDetail && <CarDetails car={carDetail} />}
      </DialogShell>

      <DialogShell
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        title={deleteTarget && deleteTarget.length > 1 ? "Delete vehicles" : "Delete vehicle"}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setDeleteTarget(null), disabled: deleting },
          { label: "Delete", onClick: runDelete, variant: "danger", loading: deleting },
        ]}
      >
        <div className="superRideConfirm">
          {deleteTarget && deleteTarget.length > 1
            ? `${deleteTarget.length} vehicles will be removed. Their drivers will not be able to go online until another vehicle is registered.`
            : "This vehicle will be removed. Its driver will not be able to go online until another vehicle is registered."}
        </div>
      </DialogShell>
    </div>
  );
};

export default Cars;
